<script setup lang="ts">
import { computed } from 'vue'
import type { FieldDefinition } from '@/types/calculator'
import Input from '@/components/ui/Input.vue'
import RadioGroup from '@/components/ui/RadioGroup.vue'
import Select from '@/components/ui/Select.vue'

const props = defineProps<{
  field: FieldDefinition
  modelValue: string | number | boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean): void
  (e: 'blur'): void
}>()

const fieldId = computed(() => `field-${props.field.name}`)

const inputValue = computed(() => {
  if (props.modelValue === undefined || props.modelValue === null) return ''
  if (props.field.type === 'percentage') {
    return String(Number(props.modelValue) * 100)
  }
  return String(props.modelValue)
})

function onInputValue(value: string) {
  if (props.field.type === 'number' || props.field.type === 'money') {
    emit('update:modelValue', value === '' ? 0 : Number(value))
  } else if (props.field.type === 'percentage') {
    emit('update:modelValue', value === '' ? 0 : Number(value) / 100)
  } else {
    emit('update:modelValue', value)
  }
}

function onChoice(value: string) {
  emit('update:modelValue', value)
}

function onCheckbox(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).checked)
}
</script>

<template>
  <div class="dynamic-field">
    <Input
      v-if="
        field.type === 'number' ||
        field.type === 'money' ||
        field.type === 'percentage' ||
        field.type === 'text'
      "
      :id="fieldId"
      :label="field.label"
      :type="field.type === 'money' || field.type === 'percentage' ? 'number' : 'text'"
      :model-value="inputValue"
      :placeholder="field.placeholder"
      :hint="field.hint"
      :unit="field.unit"
      :min="field.type === 'percentage' ? 0 : field.min"
      :max="field.type === 'percentage' ? 100 : field.max"
      :step="
        field.type === 'percentage'
          ? field.step
            ? field.step * 100
            : '0.01'
          : (field.step ?? (field.type === 'money' ? '0.01' : '1'))
      "
      :required="field.required"
      :error="error"
      :aria-describedby="field.hint ? `${fieldId}-hint` : undefined"
      @update:model-value="onInputValue"
      @blur="$emit('blur')"
    />
    <RadioGroup
      v-else-if="field.type === 'radio'"
      :id="fieldId"
      :model-value="String(modelValue)"
      :label="field.label"
      :options="field.options || []"
      :required="field.required"
      :error="error"
      @update:model-value="onChoice"
    />
    <Select
      v-else-if="field.type === 'select'"
      :id="fieldId"
      :model-value="String(modelValue)"
      :label="field.label"
      :options="field.options || []"
      :required="field.required"
      :error="error"
      @update:model-value="onChoice"
    />
    <template v-else-if="field.type === 'checkbox'">
      <label class="checkbox-label" :for="fieldId">
        <input :id="fieldId" type="checkbox" :checked="Boolean(modelValue)" @change="onCheckbox" />
        {{ field.label }}
      </label>
      <p v-if="error" class="field-error" role="alert">{{ error }}</p>
    </template>
  </div>
</template>

<style scoped>
.dynamic-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
}

.field-error {
  font-size: var(--text-sm);
  color: var(--color-error);
}
</style>
